import { useState, useCallback } from "react"
import type { RendererProps } from "../core/element-registry"
import type { CogoElement } from "../core/types"
import { useRenderContext } from "../core/render-context"
import { cn } from "../lib/utils"

function TreeNode({
  node,
  depth,
  expanded,
  onToggle,
}: {
  node: CogoElement
  depth: number
  expanded: Set<string>
  onToggle: (id: string) => void
}) {
  const hasChildren = node.children && node.children.length > 0
  const isExpanded = expanded.has(node.id)

  return (
    <div className="flex flex-col">
      <button
        type="button"
        className={cn(
          "flex flex-row items-center gap-1.5 px-1 py-0.5 rounded-md bg-transparent border-none cursor-pointer text-left hover:bg-muted transition-colors",
        )}
        style={{ paddingLeft: `${depth * (node.indentSize ?? 16)}px` }}
        onClick={() => hasChildren && onToggle(node.id)}
        aria-label={
          hasChildren
            ? `${isExpanded ? "Collapse" : "Expand"} ${node.text ?? node.id}`
            : node.text ?? node.id
        }
      >
        {hasChildren ? (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className={cn(
              "text-muted-foreground transition-transform duration-150",
              isExpanded && "rotate-90",
            )}
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        ) : (
          <span className="w-4" />
        )}
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill={hasChildren ? "currentColor" : "none"}
          stroke={hasChildren ? "currentColor" : "currentColor"}
          strokeWidth="2"
          className={hasChildren ? (node.folderColorClass ?? "text-yellow-500") : "text-muted-foreground"}
        >
          {hasChildren ? (
            <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
          ) : (
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z M14 2v6h6" />
          )}
        </svg>
        <span className="text-sm text-foreground">{node.text ?? node.id}</span>
      </button>
      {hasChildren &&
        isExpanded &&
        node.children!.map((child) => (
          <TreeNode
            key={child.id}
            node={child}
            depth={depth + 1}
            expanded={expanded}
            onToggle={onToggle}
          />
        ))}
    </div>
  )
}

export function TreeRenderer({ element }: RendererProps) {
  const { dispatchEvent } = useRenderContext()
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set([element.id]),
  )

  const handleToggle = useCallback(
    (id: string) => {
      setExpanded((prev) => {
        const next = new Set(prev)
        if (next.has(id)) {
          next.delete(id)
        } else {
          next.add(id)
        }
        return next
      })
      dispatchEvent(element.id, "tap", { toggled: id })
    },
    [element.id, dispatchEvent],
  )

  return (
    <div
      className={cn("flex flex-col", element.className)}
      data-testid={`tree-${element.id}`}
    >
      <TreeNode
        node={element}
        depth={0}
        expanded={expanded}
        onToggle={handleToggle}
      />
    </div>
  )
}
